import * as React from 'react'
import { NavLink, Outlet, useLocation, useParams } from 'react-router'
import { uuidParamSchema } from '@lib/validators/params'
import { ErrorState, LoadingState } from '@/components/PageState'
import { DashboardScopeProvider, type DashboardScopeValue } from '@/dashboard/DashboardScope'
import { ModeSwitch, TestModeBanner } from '@/dashboard/ModeSwitch'
import { ViewAsBanner } from '@/dashboard/ReadOnlyBanner'
import { useDashboardMode } from '@/dashboard/useDashboardMode'
import { useLeaveScope } from '@/dashboard/useLeaveScope'
import { api } from '@/helpers/apiClient'
import { apiErrorMessage } from '@/helpers/apiErrorMessage'
import { useMcpEnabled } from '@/hooks/useConnectedApps'
import { useOpsTenant } from '@/hooks/useOpsTenants'
import { cn } from '@/lib/utils'

function tabClass({ isActive }: { isActive: boolean }): string {
  return cn(
    'block border-b-2 px-1 pb-2 text-body font-medium whitespace-nowrap transition-colors',
    isActive ? 'border-primary text-foreground' : 'border-transparent text-muted-foreground hover:text-foreground',
  )
}

/**
 * Staff (ADMIN or SUPPORT) viewing a tenant's dashboard read-only, inside the ops console.
 * The pages are the tenant's own (Team, Activity), served through the staff
 * session; the API refuses anything but reads, and logs the visit to the
 * tenant's activity (which its owners see) when it serves them.
 */
export function OpsViewAsLayout() {
  const { tenantId: rawTenantId } = useParams()
  const parsedTenantId = uuidParamSchema.safeParse(rawTenantId)
  const tenantId = parsedTenantId.success ? parsedTenantId.data : null
  const tenant = useOpsTenant(tenantId)
  const [mode, setStoredMode] = useDashboardMode(tenantId)
  const leaveScope = useLeaveScope()
  const setMode = React.useMemo(() => leaveScope(setStoredMode), [leaveScope, setStoredMode])
  const { pathname } = useLocation()
  const tabsRef = React.useRef<HTMLElement>(null)
  const mcpEnabled = useMcpEnabled()
  const scope: DashboardScopeValue | null = React.useMemo(
    () =>
      tenant.data
        ? {
            tenantId: tenant.data.id,
            tenantName: tenant.data.name,
            role: 'owner',
            readOnly: true,
            readOnlyReason: 'view-as',
            currentUserId: null,
            client: api,
            cacheKey: 'staff',
            mode,
            setMode,
            basePath: `/ops/tenants/${tenant.data.id}/view`,
          }
        : null,
    [tenant.data, mode, setMode],
  )

  // When the tabs scroll sideways (phones), keep the current one in view.
  React.useEffect(() => {
    const nav = tabsRef.current
    if (!nav) return
    const active = nav.querySelector('[aria-current="page"]')
    if (!(active instanceof HTMLElement)) return
    nav.scrollLeft = active.offsetLeft - (nav.clientWidth - active.offsetWidth) / 2
  }, [pathname, scope])

  if (!parsedTenantId.success) return <ErrorState message="That organization link is not valid." />
  if (tenant.isLoading) return <LoadingState />
  if (tenant.isError || !scope) {
    return <ErrorState message={apiErrorMessage(tenant.error, 'Could not load the tenant.')} />
  }

  return (
    <DashboardScopeProvider value={scope}>
      <ViewAsBanner exitTo={`/ops/tenants/${scope.tenantId}`} />
      {mode === 'test' ? <TestModeBanner /> : null}
      {/* On phones the switch sits above the tabs, which scroll sideways when they don't fit. The tab row (not each tab) overlaps the border, so scrolling doesn't clip the underline. */}
      <div className="mb-6 flex flex-col gap-3 border-b border-border sm:flex-row sm:items-end sm:justify-between sm:gap-4">
        <div className="w-36 shrink-0 sm:order-last sm:mb-2">
          <ModeSwitch mode={mode} onChange={setMode} />
        </div>
        <nav
          ref={tabsRef}
          aria-label="Tenant dashboard"
          className="relative -mb-px flex min-w-0 gap-5 overflow-x-auto [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
        >
          <NavLink to={`${scope.basePath}/envelopes`} className={tabClass}>
            Envelopes
          </NavLink>
          <NavLink to={`${scope.basePath}/team`} className={tabClass}>
            Team
          </NavLink>
          <NavLink to={`${scope.basePath}/api-keys`} className={tabClass}>
            API keys
          </NavLink>
          <NavLink to={`${scope.basePath}/webhooks`} className={tabClass}>
            Webhooks
          </NavLink>
          <NavLink to={`${scope.basePath}/requests`} className={tabClass}>
            Requests
          </NavLink>
          <NavLink to={`${scope.basePath}/settings`} className={tabClass}>
            Settings
          </NavLink>
          <NavLink to={`${scope.basePath}/billing`} className={tabClass}>
            Billing
          </NavLink>
          {mode === 'test' ? (
            <NavLink to={`${scope.basePath}/mailbox`} className={tabClass}>
              Mailbox
            </NavLink>
          ) : null}
          <NavLink to={`${scope.basePath}/activity`} className={tabClass}>
            Activity
          </NavLink>
          {mcpEnabled ? (
            <NavLink to={`${scope.basePath}/connected-apps`} className={tabClass}>
              Connected apps
            </NavLink>
          ) : null}
        </nav>
      </div>
      {/* Keyed by tenant: moving to another organization starts its pages fresh, forms included. */}
      <Outlet key={scope.tenantId} />
    </DashboardScopeProvider>
  )
}
